/**
 * BombShrapnel.ts
 * Pooled radial fragment spawned by a detonating {@link ClusterBomb}.
 *
 * Travels in a straight line on a fixed heading at a fixed speed for
 * {@link BOMBER_DRONE_SHRAPNEL_TTL_S} seconds, then self-destructs.
 * Also despawns early on left-edge cull or on ship contact (consumed
 * by `shipDamage`).
 *
 * Pool contract: implements `reset()` for `ObjectPool` recycling.
 * Always acquire from `bombShrapnelPool` (see `bomberDroneDrop.ts`)
 * rather than `new`-ing directly outside of pool pre-warm.
 *
 * NO React imports, NO async.
 *
 * Phase 4 — World 2: Starfield Drift.
 *
 * @module BombShrapnel
 */

import { BaseEntity } from './BaseEntity';
import type { LevelRuntime } from '../engine/gameState';
import { spaceState } from '../engine/spaceState';
import {
  BOMBER_DRONE_SHRAPNEL_WIDTH,
  BOMBER_DRONE_SHRAPNEL_HEIGHT,
  BOMBER_DRONE_SHRAPNEL_SPEED_PX_S,
  BOMBER_DRONE_SHRAPNEL_TTL_S,
  BOMBER_DRONE_BOMB_OFFSCREEN_MARGIN_PX,
} from '../../constants/spaceEnemyConstants';

/**
 * BombShrapnel — pooled fragment that flies on a fixed heading until
 * its TTL elapses or it leaves the viewport. Damage is applied by
 * `shipDamage` on overlap.
 */
export class BombShrapnel extends BaseEntity {
  /** Monotonic ID counter for stable renderer keys across pool recycling. */
  private static nextId = 1;

  /** Unique spawn identifier — re-issued each `spawn()` call. */
  public spawnId = 0;

  /** True while the fragment is travelling. */
  public active = false;

  /** Seconds remaining on the lifetime. Counts down by `dt` each tick. */
  private ttlRemaining = 0;

  constructor() {
    super(0, 0, BOMBER_DRONE_SHRAPNEL_WIDTH, BOMBER_DRONE_SHRAPNEL_HEIGHT);
    this.dead = true;
  }

  /**
   * Initialise the fragment at a world position and a polar heading.
   * Velocity is `SPEED * (cos θ, sin θ)`, integrated linearly in
   * `update()`. The caller supplies the heading — the shrapnel itself
   * has no preferred direction.
   *
   * @param x       - Spawn X in world space (logical px, top-left of hitbox).
   * @param y       - Spawn Y in world space (logical px, top-left of hitbox).
   * @param heading - Heading in radians (0 = east, π/2 = south, π = west).
   */
  public spawn(x: number, y: number, heading: number): void {
    this.spawnId = BombShrapnel.nextId++;
    this.x = x;
    this.y = y;
    this.vx = Math.cos(heading) * BOMBER_DRONE_SHRAPNEL_SPEED_PX_S;
    this.vy = Math.sin(heading) * BOMBER_DRONE_SHRAPNEL_SPEED_PX_S;
    this.ttlRemaining = BOMBER_DRONE_SHRAPNEL_TTL_S;
    this.dead = false;
    this.active = true;
  }

  /** Reset state for pool recycling. Called by `ObjectPool.release()`. */
  public reset(): void {
    this.x = 0;
    this.y = 0;
    this.vx = 0;
    this.vy = 0;
    this.ttlRemaining = 0;
    this.dead = true;
    this.active = false;
  }

  /**
   * Advance the fragment by one fixed timestep. Order of resolution
   * per tick: integrate position → check left-cull → tick TTL → die
   * on expiry. Mirrors the `ClusterBomb.update` sequence so a bomb +
   * its shrapnel share the same observable lifecycle shape.
   *
   * @param dt    - Fixed timestep in seconds.
   * @param level - Unused; kept for `BaseEntity.update` contract.
   */
  public update(dt: number, level: LevelRuntime): void {
    void level;
    if (!this.active) return;

    this.x += this.vx * dt;
    this.y += this.vy * dt;

    const leftCullX = spaceState.cameraX - BOMBER_DRONE_BOMB_OFFSCREEN_MARGIN_PX;
    if (this.x + this.width < leftCullX) {
      this.dead = true;
      this.active = false;
      return;
    }

    this.ttlRemaining -= dt;
    if (this.ttlRemaining <= 0) {
      this.dead = true;
      this.active = false;
    }
  }
}
